import { Slice, Fragment, DOMParser as PMDOMParser } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import DOMPurify from 'dompurify';

function stripCustomTags(text) {
  // Remove [fontSize=...]...[/fontSize], [fontFamily=...]...[/fontFamily], [textColor=...]...[/textColor]
  return text
    .replace(/\[fontSize=[^\]]+\]([^[]*)\[\/fontSize\]/gi, '$1')
    .replace(/\[fontFamily=[^\]]+\]([^[]*)\[\/fontFamily\]/gi, '$1')
    .replace(/\[textColor=[^\]]+\]([^[]*)\[\/textColor\]/gi, '$1');
}

const PRE_WHITE_SPACE_VALUES = new Set([
  'pre',
  'pre-line',
  'pre-wrap',
  'break-spaces',
]);

// Some sources (chat UIs, code viewers) preserve line breaks via a literal
// '\n' character plus CSS `white-space: pre*` instead of real <br> tags.
// ProseMirror's DOMParser doesn't consult CSS, so without this it collapses
// those newlines into spaces. Turn them into real <br> elements first so the
// existing hard_break splitting picks them up.
function preserveLiteralNewlines(container) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node);
    node = walker.nextNode();
  }

  textNodes.forEach(textNode => {
    if (!textNode.nodeValue.includes('\n')) return;

    let whiteSpace = 'normal';
    let el = textNode.parentElement;
    while (el && el !== container) {
      if (el.style && el.style.whiteSpace) {
        whiteSpace = el.style.whiteSpace;
        break;
      }
      el = el.parentElement;
    }
    if (!PRE_WHITE_SPACE_VALUES.has(whiteSpace)) return;

    const fragment = document.createDocumentFragment();
    textNode.nodeValue.split('\n').forEach((line, index) => {
      if (index > 0) fragment.appendChild(document.createElement('br'));
      if (line) fragment.appendChild(document.createTextNode(line));
    });
    textNode.replaceWith(fragment);
  });
}

// Gmail/Outlook often wrap each line in a bare <div> with no matching schema
// rule. Add it as a low-priority fallback (after schema rules, e.g. the more
// specific div[data-html-block] rule) so plain divs still split into
// paragraphs the way <p> does.
function buildParser(schema) {
  const rules = [
    ...PMDOMParser.schemaRules(schema),
    { tag: 'div', node: 'paragraph', priority: 40 },
  ];
  return new PMDOMParser(schema, rules);
}

// Dissolve list/blockquote wrappers so pasted content always ends up as a
// flat sequence of paragraphs, matching the editor's existing paste
// behavior (no nested lists/blockquotes).
function flattenToParagraphs(fragment, schema, currentAttrs, out = []) {
  fragment.forEach(node => {
    if (node.type === schema.nodes.paragraph) {
      out.push(schema.nodes.paragraph.create(currentAttrs, node.content));
    } else if (node.type === schema.nodes.code_block) {
      const codeMark = schema.marks.code.create();
      const content = node.content.size
        ? Fragment.from(schema.text(node.textContent, [codeMark]))
        : Fragment.empty;
      out.push(schema.nodes.paragraph.create(currentAttrs, content));
    } else if (node.isBlock && node.content.size) {
      flattenToParagraphs(node.content, schema, currentAttrs, out);
    }
  });
  return out;
}

// ProseMirror parses <br> as a hard_break inside one paragraph (a soft
// break). This editor treats every line as its own paragraph, so split each
// paragraph into multiple paragraphs at every hard_break.
function splitAtHardBreaks(paragraph, schema) {
  const segments = [[]];
  paragraph.content.forEach(node => {
    if (node.type === schema.nodes.hard_break) {
      segments.push([]);
    } else {
      segments[segments.length - 1].push(node);
    }
  });
  return segments.map(nodes =>
    schema.nodes.paragraph.create(paragraph.attrs, Fragment.fromArray(nodes))
  );
}

function cleanParagraphText(paragraph, schema) {
  const content = [];
  paragraph.content.forEach(node => {
    if (node.isText) {
      const cleaned = stripCustomTags(node.text.replace(/\u00A0/g, ''));
      if (cleaned) content.push(schema.text(cleaned, node.marks));
    } else {
      content.push(node);
    }
  });
  return schema.nodes.paragraph.create(
    paragraph.attrs,
    Fragment.fromArray(content)
  );
}

export const plainPastePlugin = new Plugin({
  props: {
    handlePaste(view, event) {
      if (!event.clipboardData) return false;

      const rawHtml = event.clipboardData.getData('text/html');
      if (!rawHtml) return false;

      // Clipboard HTML is untrusted (may come from any web page); sanitize
      // before parsing to strip scripts/event handlers.
      const container = document.createElement('div');
      container.innerHTML = DOMPurify.sanitize(rawHtml);
      preserveLiteralNewlines(container);

      const { schema } = view.state;
      const parsedDoc = buildParser(schema).parse(container);
      const currentAttrs = view.state.selection.$from.parent.attrs;

      let paragraphs = flattenToParagraphs(
        parsedDoc.content,
        schema,
        currentAttrs
      );
      paragraphs = paragraphs.flatMap(p => splitAtHardBreaks(p, schema));
      paragraphs = paragraphs.map(p => cleanParagraphText(p, schema));

      if (paragraphs.length === 0) return false;

      const slice = new Slice(Fragment.fromArray(paragraphs), 1, 1);
      view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView());
      return true;
    },
  },
});
